
import { useState, useCallback, useMemo, memo } from 'react';
import {
    Box,
    Flex,
    Grid,
    Heading,
    Text,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    Checkbox,
    Badge,
    Skeleton,
    // SimpleGrid,
    Link as ChakraLink,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import useDebounce from 'hooks/useDebounce';
import useOrders from 'hooks/useOrders';
import useOrderExport from 'hooks/useOrderExport';
import MakePaymentModal from './Checkout/MakePaymentModal';
import { formatOrderAmount } from 'utils/currencyParser';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const SUCCESS     = '#10B981';
const SUCCESS_BG  = '#ECFDF5';
const DANGER      = '#E53E3E';
const DANGER_BG   = '#FEF2F2';
const WARNING     = '#EA580C';
const WARNING_BG  = '#FFF7ED';
const INFO        = '#2563EB';
const INFO_BG     = '#EFF6FF';
const PURPLE      = '#7C3AED';
const PURPLE_BG   = '#EDE9FE';

const FONT = "'Bricolage Grotesque', sans-serif";

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

const SEARCH_DEBOUNCE_MS = 400;

/* ─────────────────────────────────────────────────────────────
   STATUS METADATA
   ───────────────────────────────────────────────────────────── */

const ORDER_STATUS_META = {
    pending:      { label: 'Pending',      color: MUTED,   bg: SURFACE_ALT },
    confirmed:    { label: 'Confirmed',    color: INFO,    bg: INFO_BG     },
    processing:   { label: 'Processing',   color: WARNING, bg: WARNING_BG  },
    shipped:      { label: 'Shipped',      color: PURPLE,  bg: PURPLE_BG   },
    fulfilled:    { label: 'Fulfilled',    color: SUCCESS, bg: SUCCESS_BG  },
    cancelled:    { label: 'Cancelled',    color: DANGER,  bg: DANGER_BG   },
    refunded:     { label: 'Refunded',     color: DANGER,  bg: DANGER_BG   },
};

const PAYMENT_STATUS_META = {
    pending: { label: 'Pending', color: WARNING, bg: WARNING_BG },
    paid:    { label: 'Paid',    color: SUCCESS, bg: SUCCESS_BG },
    failed:  { label: 'Failed',  color: DANGER,  bg: DANGER_BG  },
    refunded:{ label: 'Refunded',color: MUTED,   bg: SURFACE_ALT},
};

const ORDER_STATUS_OPTIONS   = Object.keys(ORDER_STATUS_META);
const PAYMENT_STATUS_OPTIONS = Object.keys(PAYMENT_STATUS_META);

const getOrderStatusMeta = (status) =>
    ORDER_STATUS_META[(status ?? '').toLowerCase()]
    || { label: status || 'Unknown', color: MUTED, bg: SURFACE_ALT };

const getPaymentStatusMeta = (status) =>
    PAYMENT_STATUS_META[(status ?? '').toLowerCase()]
    || { label: status || 'Unknown', color: MUTED, bg: SURFACE_ALT };

/* ─────────────────────────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────────────────────────── */

const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        return new Date(dateString).toLocaleDateString('en-NG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '—';
    }
};

const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleTimeString('en-NG', {
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '';
    }
};

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */

const SearchIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const RefreshIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0114.85-3.36L23 10M1 14l4.65 4.36A9 9 0 0020.5 15"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = (props) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);

const DownloadIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CardIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M2 10h20" stroke="currentColor" strokeWidth="2" />
    </svg>
);

const MoreIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <circle cx="5" cy="12" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="19" cy="12" r="2" />
    </svg>
);

const ChevronDownIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CheckIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const InboxIcon = (props) => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M22 12h-6l-2 3h-4l-2-3H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 5h13l3 7v6a2 2 0 01-2 2h-15a2 2 0 01-2-2v-6l3-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   STATUS BADGE
   ───────────────────────────────────────────────────────────── */

const StatusBadge = memo(function StatusBadge({ status, type = 'order' }) {
    const meta = type === 'payment'
        ? getPaymentStatusMeta(status)
        : getOrderStatusMeta(status);

    return (
        <Badge
            bg={meta.bg}
            color={meta.color}
            fontSize="9.5px"
            fontWeight="800"
            px="8px"
            py="2px"
            borderRadius="full"
            textTransform="uppercase"
            letterSpacing="0.04em"
            border="1px solid"
            borderColor={`${meta.color}30`}
            whiteSpace="nowrap"
            display="inline-block"
        >
            {meta.label}
        </Badge>
    );
});

/* ─────────────────────────────────────────────────────────────
   STATUS DROPDOWN — refined (matches Shipments)
   ───────────────────────────────────────────────────────────── */

const StatusDropdown = memo(function StatusDropdown({
    value, onChange, options, getMeta, placeholder = 'All statuses',
}) {
    const selectedMeta = value ? getMeta(value) : null;

    return (
        <Menu placement="bottom-start" matchWidth autoSelect={false}>
            {({ isOpen }) => (
                <>
                    <MenuButton
                        as={Button}
                        rightIcon={
                            <Box
                                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                transition="transform 0.18s ease"
                                color={FAINT}
                            >
                                <ChevronDownIcon />
                            </Box>
                        }
                        w="full"
                        h="36px"
                        px="12px"
                        bg={SURFACE_ALT}
                        border="1px solid"
                        borderColor={isOpen ? NAVY : BORDER}
                        boxShadow={isOpen ? `0 0 0 3px ${CYAN}` : 'none'}
                        borderRadius="8px"
                        textAlign="left"
                        fontWeight="500"
                        fontFamily={FONT}
                        _hover={{ borderColor: isOpen ? NAVY : '#CBD5E0', bg: SURFACE_ALT }}
                        _active={{ bg: SURFACE_ALT }}
                        sx={{
                            '& > span': {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flex: 1,
                                minWidth: 0,
                            },
                        }}
                    >
                        {selectedMeta ? (
                            <Flex align="center" gap="8px" minW="0">
                                <Box w="8px" h="8px" borderRadius="full" bg={selectedMeta.color} flexShrink={0} />
                                <Text fontSize="12px" color={INK} fontWeight="600" noOfLines={1}>
                                    {selectedMeta.label}
                                </Text>
                            </Flex>
                        ) : (
                            <Text fontSize="12px" color={FAINT} fontWeight="500">
                                {placeholder}
                            </Text>
                        )}
                    </MenuButton>

                    <MenuList
                        p="6px"
                        borderRadius="10px"
                        border="1px solid"
                        borderColor={BORDER}
                        boxShadow={SHADOW_SM}
                        bg={SURFACE}
                        fontFamily={FONT}
                        minW="0"
                        zIndex={20}
                        maxH="280px"
                        overflowY="auto"
                    >
                        <MenuItem
                            onClick={() => onChange('')}
                            bg={!value ? CYAN : 'transparent'}
                            color={!value ? NAVY : MUTED}
                            fontSize="12px"
                            fontWeight={!value ? '700' : '500'}
                            borderRadius="6px"
                            mb="2px"
                            px="10px"
                            py="8px"
                            _hover={{ bg: !value ? CYAN : SURFACE_ALT, color: NAVY }}
                            _focus={{ bg: !value ? CYAN : SURFACE_ALT, color: NAVY }}
                        >
                            <Flex align="center" justify="space-between" w="full" gap="8px">
                                <Flex align="center" gap="8px">
                                    <Box w="8px" h="8px" borderRadius="full" bg={FAINT} />
                                    <Text as="span">{placeholder}</Text>
                                </Flex>
                                {!value && <Box color={NAVY}><CheckIcon /></Box>}
                            </Flex>
                        </MenuItem>

                        {options.map((s) => {
                            const meta = getMeta(s);
                            const isSelected = value === s;
                            return (
                                <MenuItem
                                    key={s}
                                    onClick={() => onChange(s)}
                                    bg={isSelected ? CYAN : 'transparent'}
                                    color={isSelected ? NAVY : MUTED}
                                    fontSize="12px"
                                    fontWeight={isSelected ? '700' : '500'}
                                    borderRadius="6px"
                                    mb="2px"
                                    px="10px"
                                    py="8px"
                                    _hover={{ bg: isSelected ? CYAN : SURFACE_ALT, color: NAVY }}
                                    _focus={{ bg: isSelected ? CYAN : SURFACE_ALT, color: NAVY }}
                                >
                                    <Flex align="center" justify="space-between" w="full" gap="8px">
                                        <Flex align="center" gap="8px" minW="0">
                                            <Box w="8px" h="8px" borderRadius="full" bg={meta.color} flexShrink={0} />
                                            <Text as="span" noOfLines={1}>{meta.label}</Text>
                                        </Flex>
                                        {isSelected && <Box color={NAVY}><CheckIcon /></Box>}
                                    </Flex>
                                </MenuItem>
                            );
                        })}
                    </MenuList>
                </>
            )}
        </Menu>
    );
});

/* ─────────────────────────────────────────────────────────────
   ACTIVE FILTER PILL
   ───────────────────────────────────────────────────────────── */

const ActiveFilterPill = ({ label, value, onClear }) => (
    <Flex
        align="center"
        gap="6px"
        bg={CYAN}
        color={NAVY}
        fontSize="11px"
        fontWeight="700"
        fontFamily={FONT}
        px="10px"
        py="4px"
        borderRadius="full"
        border="1px solid"
        borderColor={`${NAVY}20`}
    >
        <Text as="span" color={MUTED} fontWeight="600">{label}:</Text>
        <Text as="span" noOfLines={1} maxW="140px">{value}</Text>
        <IconButton
            aria-label={`Clear ${label}`}
            icon={<CloseIcon />}
            onClick={onClear}
            size="xs"
            h="16px"
            minW="16px"
            w="16px"
            borderRadius="full"
            bg="transparent"
            color={NAVY}
            _hover={{ bg: 'whiteAlpha.700' }}
        />
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   ROW ACTION MENU — export + payment (shared between layouts)
   ───────────────────────────────────────────────────────────── */

const RowActionMenu = memo(function RowActionMenu({ order, onMakePayment }) {
    const { exportAsCSV, exportAsXLSX, exportAsPDF } = useOrderExport({ tableData: [order] });

    const exportPayload = useMemo(() => {
        const symbol = order._currency?.symbol ?? '';
        const rate   = order._currency?.rate ?? 1;
        return {
            ...order,
            orderAmount: formatOrderAmount(order.amount, symbol),
            orderCommission: formatOrderAmount((order.commission ?? 0) * rate, symbol),
            finalRate: rate,
            finalCurrency: symbol,
        };
    }, [order]);

    return (
        <Menu placement="bottom-end" autoSelect={false}>
            <MenuButton
                as={IconButton}
                aria-label="Order actions"
                icon={<MoreIcon />}
                size="xs"
                variant="ghost"
                color={MUTED}
                _hover={{ bg: SURFACE_ALT, color: NAVY }}
                onClick={(e) => e.stopPropagation()}
            />
            <MenuList
                p="6px"
                borderRadius="10px"
                border="1px solid"
                borderColor={BORDER}
                boxShadow={SHADOW_SM}
                bg={SURFACE}
                fontFamily={FONT}
                minW="180px"
                zIndex={20}
            >
                <MenuItem
                    icon={<DownloadIcon />}
                    onClick={() => exportAsCSV(true, exportPayload)}
                    fontSize="12px"
                    color={INK}
                    borderRadius="6px"
                    _hover={{ bg: SURFACE_ALT }}
                    _focus={{ bg: SURFACE_ALT }}
                >
                    Export as CSV
                </MenuItem>
                <MenuItem
                    icon={<DownloadIcon />}
                    onClick={() => exportAsXLSX(true, exportPayload)}
                    fontSize="12px"
                    color={INK}
                    borderRadius="6px"
                    _hover={{ bg: SURFACE_ALT }}
                    _focus={{ bg: SURFACE_ALT }}
                >
                    Export as XLSX
                </MenuItem>
                <MenuItem
                    icon={<DownloadIcon />}
                    onClick={() => exportAsPDF(true, exportPayload)}
                    fontSize="12px"
                    color={INK}
                    borderRadius="6px"
                    _hover={{ bg: SURFACE_ALT }}
                    _focus={{ bg: SURFACE_ALT }}
                >
                    Export as PDF
                </MenuItem>
                <MenuItem
                    icon={<CardIcon />}
                    onClick={() => onMakePayment(order)}
                    fontSize="12px"
                    color={NAVY}
                    fontWeight="700"
                    borderRadius="6px"
                    _hover={{ bg: CYAN }}
                    _focus={{ bg: CYAN }}
                >
                    Make Payment
                </MenuItem>
            </MenuList>
        </Menu>
    );
});

/* ─────────────────────────────────────────────────────────────
   DESKTOP TABLE
   ───────────────────────────────────────────────────────────── */

const OrdersTable = memo(function OrdersTable({
    orders, selectedIds, isAllSelected,
    onToggleSelect, onToggleSelectAll, onMakePayment,
}) {
    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="12px"
            overflow="hidden"
            boxShadow={SHADOW_SM}
        >
            <TableContainer>
                <Table variant="simple" size="sm" minW="1100px">
                    <Thead bg={SURFACE_ALT}>
                        <Tr>
                            <Th py="12px" pl="14px" pr="0" w="40px" borderColor={BORDER}>
                                <Checkbox
                                    isChecked={isAllSelected}
                                    onChange={(e) => onToggleSelectAll(e.target.checked)}
                                    colorScheme="blue"
                                    sx={{
                                        '& .chakra-checkbox__control': {
                                            borderColor: BORDER,
                                            _checked: { bg: NAVY, borderColor: NAVY, _hover: { bg: NAVY } },
                                        },
                                    }}
                                />
                            </Th>
                            {[
                                'Order Ref', 'Date', 'Customer', 'Items',
                                'Payment', 'Status', 'Order Value',
                                'Shipping', 'Commission', '',
                            ].map((h, i) => (
                                <Th
                                    key={i}
                                    py="12px"
                                    fontSize="10px"
                                    fontWeight="800"
                                    color={MUTED}
                                    letterSpacing="0.08em"
                                    textTransform="uppercase"
                                    borderColor={BORDER}
                                >
                                    {h}
                                </Th>
                            ))}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {orders.map((order) => {
                            const isSelected = selectedIds.has(order.order_id);
                            const symbol = order._currency?.symbol ?? '';

                            return (
                                <Tr
                                    key={order.order_id}
                                    bg={isSelected ? CYAN : 'transparent'}
                                    _hover={{ bg: isSelected ? CYAN : SURFACE_ALT }}
                                    transition="background 0.15s ease"
                                >
                                    <Td py="14px" pl="14px" pr="0" borderColor={BORDER}>
                                        <Checkbox
                                            isChecked={isSelected}
                                            onChange={() => onToggleSelect(order.order_id)}
                                            colorScheme="blue"
                                            sx={{
                                                '& .chakra-checkbox__control': {
                                                    borderColor: BORDER,
                                                    _checked: { bg: NAVY, borderColor: NAVY, _hover: { bg: NAVY } },
                                                },
                                            }}
                                        />
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <ChakraLink
                                            as={Link}
                                            to={`/dashboard/order-details/${order.order_id}`}
                                            color={NAVY}
                                            fontSize="12px"
                                            fontWeight="700"
                                            _hover={{ color: NAVY_DEEP, textDecoration: 'underline' }}
                                        >
                                            {order.order_ref ?? 'N/A'}
                                        </ChakraLink>
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <Text fontSize="12px" color={INK} fontWeight="600">
                                            {formatDate(order.createdAt)}
                                        </Text>
                                        <Text fontSize="10px" color={FAINT}>
                                            {formatTime(order.createdAt)}
                                        </Text>
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <Text fontSize="12px" color={INK} fontWeight="600" noOfLines={1}>
                                            {order._shipmentDetail?.delivery_address?.email ?? 'N/A'}
                                        </Text>
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <Text fontSize="12px" color={MUTED} textAlign="center">
                                            {order._itemCount || '—'}
                                        </Text>
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <StatusBadge status={order.payments} type="payment" />
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <StatusBadge status={order.status} type="order" />
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <Text fontSize="12px" color={INK} fontWeight="700">
                                            {formatOrderAmount(order.amount, symbol)}
                                        </Text>
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <Text fontSize="12px" color={MUTED}>
                                            {formatOrderAmount(order.shipping_fee, symbol)}
                                        </Text>
                                    </Td>
                                    <Td py="14px" borderColor={BORDER}>
                                        <Text fontSize="12px" color={SUCCESS} fontWeight="700">
                                            {formatOrderAmount(order.commission, symbol)}
                                        </Text>
                                    </Td>
                                    <Td py="14px" pr="14px" borderColor={BORDER}>
                                        <RowActionMenu order={order} onMakePayment={onMakePayment} />
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   MOBILE CARD
   ───────────────────────────────────────────────────────────── */

const OrderCard = memo(function OrderCard({
    order, isSelected, onToggleSelect, onMakePayment,
}) {
    const symbol = order._currency?.symbol ?? '';
    const customer = order._shipmentDetail?.delivery_address ?? {};
    const customerName = [customer.first_name, customer.last_name]
        .filter(Boolean).join(' ') || 'Unknown customer';

    return (
        <Box
            bg={isSelected ? CYAN : SURFACE}
            border="1px solid"
            borderColor={isSelected ? NAVY : BORDER}
            borderRadius="12px"
            p="14px"
            boxShadow={SHADOW_SM}
            transition="all 0.15s ease"
        >
            {/* Header: checkbox + order ref + actions */}
            <Flex justify="space-between" align="flex-start" mb="10px" gap="8px">
                <Flex align="center" gap="10px" flex="1" minW="0">
                    <Checkbox
                        isChecked={isSelected}
                        onChange={() => onToggleSelect(order.order_id)}
                        colorScheme="blue"
                        sx={{
                            '& .chakra-checkbox__control': {
                                borderColor: BORDER,
                                _checked: { bg: NAVY, borderColor: NAVY, _hover: { bg: NAVY } },
                            },
                        }}
                    />
                    <Box flex="1" minW="0">
                        <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.08em" mb="2px">
                            ORDER REF
                        </Text>
                        <ChakraLink
                            as={Link}
                            to={`/dashboard/order-details/${order.order_id}`}
                            color={NAVY}
                            fontSize="13px"
                            fontWeight="700"
                            _hover={{ color: NAVY_DEEP, textDecoration: 'underline' }}
                        >
                            {order.order_ref ?? 'N/A'}
                        </ChakraLink>
                    </Box>
                </Flex>
                <RowActionMenu order={order} onMakePayment={onMakePayment} />
            </Flex>

            {/* Customer */}
            <Box mb="10px" pl="26px">
                <Text fontSize="12px" fontWeight="700" color={INK} noOfLines={1}>
                    {customerName}
                </Text>
                {customer.email && (
                    <Text fontSize="10.5px" color={MUTED} noOfLines={1}>
                        {customer.email}
                    </Text>
                )}
            </Box>

            {/* Status badges */}
            <Flex gap="6px" mb="12px" pl="26px" flexWrap="wrap">
                <StatusBadge status={order.status} type="order" />
                <StatusBadge status={order.payments} type="payment" />
            </Flex>

            {/* Amounts grid */}
            <Grid
                templateColumns="repeat(3, 1fr)"
                gap="8px"
                pt="10px"
                borderTop="1px solid"
                borderColor={BORDER}
            >
                <Box>
                    <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.08em">
                        TOTAL
                    </Text>
                    <Text fontSize="12px" fontWeight="800" color={INK}>
                        {formatOrderAmount(order.amount, symbol)}
                    </Text>
                </Box>
                <Box>
                    <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.08em">
                        SHIPPING
                    </Text>
                    <Text fontSize="11px" fontWeight="600" color={MUTED}>
                        {formatOrderAmount(order.shipping_fee, symbol)}
                    </Text>
                </Box>
                <Box>
                    <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.08em">
                        COMMISSION
                    </Text>
                    <Text fontSize="11px" fontWeight="700" color={SUCCESS}>
                        {formatOrderAmount(order.commission, symbol)}
                    </Text>
                </Box>
            </Grid>

            {/* Date + items */}
            <Flex justify="space-between" mt="10px" fontSize="10px" color={FAINT}>
                <Text>{formatDate(order.createdAt)} • {formatTime(order.createdAt)}</Text>
                <Text fontWeight="700">{order._itemCount || 0} item{order._itemCount === 1 ? '' : 's'}</Text>
            </Flex>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   SKELETONS
   ───────────────────────────────────────────────────────────── */

const TableSkeleton = ({ count = 6 }) => (
    <Box bg={SURFACE} border="1px solid" borderColor={BORDER} borderRadius="12px" overflow="hidden">
        {Array.from({ length: count }).map((_, i) => (
            <Flex
                key={i}
                p="14px"
                gap="14px"
                borderBottom={i === count - 1 ? 'none' : '1px solid'}
                borderColor={BORDER}
                align="center"
            >
                <Skeleton w="16px" h="16px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="80px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="100px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton flex="1" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="80px" h="20px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="80px" h="20px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="100px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Flex>
        ))}
    </Box>
);

const CardSkeleton = ({ count = 4 }) => (
    <Flex direction="column" gap="10px">
        {Array.from({ length: count }).map((_, i) => (
            <Skeleton key={i} h="180px" borderRadius="12px" startColor={SURFACE_ALT} endColor={BORDER} />
        ))}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────────────────────── */

const EmptyState = ({ hasFilters, onClearAll }) => (
    <Flex
        direction="column"
        align="center"
        justify="center"
        py="60px"
        px="20px"
        textAlign="center"
        gap="14px"
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="14px"
        boxShadow={SHADOW_SM}
    >
        <Flex w="72px" h="72px" borderRadius="full" bg={SURFACE_ALT} color={FAINT} align="center" justify="center">
            <InboxIcon />
        </Flex>
        <Box>
            <Heading as="h3" fontSize="16px" fontWeight="800" color={INK} fontFamily={FONT} mb="4px">
                {hasFilters ? 'No orders match your filters' : 'No orders yet'}
            </Heading>
            <Text fontSize="12px" color={MUTED} maxW="320px">
                {hasFilters
                    ? 'Try adjusting your search, status, or date filters.'
                    : 'Orders from your customers will appear here once they start buying.'}
            </Text>
        </Box>
        {hasFilters && (
            <Button
                onClick={onClearAll}
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                size="sm"
                h="36px"
                px="16px"
                fontSize="12px"
                fontWeight="700"
                fontFamily={FONT}
                borderRadius="8px"
            >
                Clear all filters
            </Button>
        )}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   BULK ACTION BAR — appears when rows are selected
   ───────────────────────────────────────────────────────────── */

const BulkActionBar = memo(function BulkActionBar({
    selectedOrders, onClear,
}) {
    const { exportAsCSV, exportAsXLSX } = useOrderExport({ tableData: selectedOrders });
    const count = selectedOrders.length;

    return (
        <Flex
            justify="space-between"
            align="center"
            wrap="wrap"
            gap="10px"
            bg={NAVY}
            color="white"
            p="12px 16px"
            borderRadius="10px"
            boxShadow={SHADOW_SM}
            position="sticky"
            top={{ base: '76px', md: '88px' }}
            zIndex={5}
            mb="14px"
            fontFamily={FONT}
        >
            <Flex align="center" gap="12px">
                <Text fontSize="13px" fontWeight="700">
                    {count} order{count === 1 ? '' : 's'} selected
                </Text>
                <Button
                    onClick={onClear}
                    size="xs"
                    variant="ghost"
                    color="whiteAlpha.900"
                    fontSize="11px"
                    fontWeight="700"
                    h="24px"
                    _hover={{ bg: 'whiteAlpha.200' }}
                >
                    Clear
                </Button>
            </Flex>
            <Flex gap="8px">
                <Button
                    onClick={() => exportAsCSV(false)}
                    size="sm"
                    bg="whiteAlpha.200"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    fontSize="12px"
                    fontWeight="700"
                    h="32px"
                    leftIcon={<DownloadIcon />}
                    borderRadius="8px"
                >
                    Export CSV
                </Button>
                <Button
                    onClick={() => exportAsXLSX(false)}
                    size="sm"
                    bg="white"
                    color={NAVY}
                    _hover={{ bg: 'whiteAlpha.900' }}
                    fontSize="12px"
                    fontWeight="700"
                    h="32px"
                    leftIcon={<DownloadIcon />}
                    borderRadius="8px"
                >
                    Export XLSX
                </Button>
            </Flex>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */

const Orders = () => {
    const {
        orders,
        totalCount,
        loading,
        searchTerm, setSearchTerm,
        statusFilter, setStatusFilter,
        paymentFilter, setPaymentFilter,
        startDate, setStartDate,
        endDate, setEndDate,
        activeFilters,
        hasActiveFilters,
        clearFilter,
        clearAllFilters,
        selectedIds, selectedOrders, isAllSelected,
        toggleSelection, toggleSelectAll, clearSelection,
        refresh,
    } = useOrders();

    /* Local controlled search input — debounced into the hook */
    const [localSearch, setLocalSearch] = useState(searchTerm);
    const debouncedSearch = useDebounce(localSearch, SEARCH_DEBOUNCE_MS);

    /* Sync debounced value back to hook */
    useMemo(() => {
        setSearchTerm(debouncedSearch);
    }, [debouncedSearch, setSearchTerm]);

    /* MakePaymentModal state */
    const [paymentModalOrder, setPaymentModalOrder] = useState(null);
    const closePaymentModal = useCallback(() => setPaymentModalOrder(null), []);

    const handleMakePayment = useCallback((order) => {
        setPaymentModalOrder(order);
    }, []);

    const hasResults = orders.length > 0;
    const showSkeleton = loading && !hasResults;
    const hasSelection = selectedOrders.length > 0;

    return (
        <Box bg={SURFACE_ALT} minH="100vh" p={{ base: '16px', md: '24px' }} fontFamily={FONT}>
            <Box maxW="1400px" mx="auto">
                {/* ── HEADER ── */}
                <Flex
                    justify="space-between"
                    align={{ base: 'flex-start', md: 'center' }}
                    direction={{ base: 'column', md: 'row' }}
                    gap="10px"
                    mb="20px"
                >
                    <Box>
                        <Heading
                            as="h1"
                            fontSize={{ base: '22px', md: '26px' }}
                            fontWeight="800"
                            color={INK}
                            fontFamily={FONT}
                            letterSpacing="-0.02em"
                            lineHeight="1.1"
                            mb="4px"
                        >
                            Orders
                        </Heading>
                        <Text fontSize="12px" color={MUTED}>
                            {loading && !hasResults
                                ? 'Loading orders…'
                                : totalCount > 0
                                ? `${totalCount.toLocaleString('en-NG')} ${totalCount === 1 ? 'order' : 'orders'} total`
                                : 'No orders yet'}
                        </Text>
                    </Box>

                    <Button
                        onClick={refresh}
                        isLoading={loading}
                        bg={NAVY}
                        color="white"
                        _hover={{ bg: NAVY_DEEP }}
                        _active={{ bg: NAVY_DEEP }}
                        size="sm"
                        h="38px"
                        px="16px"
                        fontSize="12.5px"
                        fontWeight="700"
                        fontFamily={FONT}
                        borderRadius="8px"
                        leftIcon={<RefreshIcon />}
                    >
                        Refresh
                    </Button>
                </Flex>

                {/* ── FILTER BAR ── */}
                <Box
                    bg={SURFACE}
                    border="1px solid"
                    borderColor={BORDER}
                    borderRadius="12px"
                    p="14px"
                    mb="14px"
                    boxShadow={SHADOW_SM}
                >
                    <Grid
                        templateColumns={{
                            base: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: '2fr 1fr 1fr 1fr 1fr',
                        }}
                        gap="10px"
                    >
                        {/* Search */}
                        <Box>
                            <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                                SEARCH
                            </Text>
                            <InputGroup size="sm">
                                <InputLeftElement pointerEvents="none" h="36px">
                                    <Box color={FAINT}><SearchIcon /></Box>
                                </InputLeftElement>
                                <Input
                                    type="search"
                                    value={localSearch}
                                    onChange={(e) => setLocalSearch(e.target.value)}
                                    placeholder="Order ref, ID, or customer email"
                                    h="36px"
                                    pl="32px"
                                    fontSize="12px"
                                    bg={SURFACE_ALT}
                                    border="1px solid"
                                    borderColor={BORDER}
                                    borderRadius="8px"
                                    fontFamily={FONT}
                                    _placeholder={{ color: FAINT }}
                                    _hover={{ borderColor: '#CBD5E0' }}
                                    _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                                />
                            </InputGroup>
                        </Box>

                        {/* Order status */}
                        <Box>
                            <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                                STATUS
                            </Text>
                            <StatusDropdown
                                value={statusFilter}
                                onChange={setStatusFilter}
                                options={ORDER_STATUS_OPTIONS}
                                getMeta={getOrderStatusMeta}
                                placeholder="All statuses"
                            />
                        </Box>

                        {/* Payment status */}
                        <Box>
                            <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                                PAYMENT
                            </Text>
                            <StatusDropdown
                                value={paymentFilter}
                                onChange={setPaymentFilter}
                                options={PAYMENT_STATUS_OPTIONS}
                                getMeta={getPaymentStatusMeta}
                                placeholder="All payments"
                            />
                        </Box>

                        {/* Start date */}
                        <Box>
                            <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                                FROM
                            </Text>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                size="sm"
                                h="36px"
                                fontSize="12px"
                                bg={SURFACE_ALT}
                                borderColor={BORDER}
                                fontFamily={FONT}
                                borderRadius="8px"
                                _hover={{ borderColor: '#CBD5E0' }}
                                _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                            />
                        </Box>

                        {/* End date */}
                        <Box>
                            <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                                TO
                            </Text>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                size="sm"
                                h="36px"
                                fontSize="12px"
                                bg={SURFACE_ALT}
                                borderColor={BORDER}
                                fontFamily={FONT}
                                borderRadius="8px"
                                _hover={{ borderColor: '#CBD5E0' }}
                                _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                            />
                        </Box>
                    </Grid>
                </Box>

                {/* ── ACTIVE FILTER PILLS ── */}
                {hasActiveFilters && (
                    <Flex align="center" gap="8px" flexWrap="wrap" mb="14px">
                        <Text fontSize="11px" color={FAINT} fontWeight="700" letterSpacing="0.08em">
                            ACTIVE:
                        </Text>
                        {activeFilters.map((f) => (
                            <ActiveFilterPill
                                key={f.key}
                                label={f.label}
                                value={f.value}
                                onClear={() => {
                                    if (f.key === 'search') setLocalSearch('');
                                    clearFilter(f.key);
                                }}
                            />
                        ))}
                        <Button
                            onClick={() => { setLocalSearch(''); clearAllFilters(); }}
                            size="xs"
                            variant="ghost"
                            color={MUTED}
                            fontSize="11px"
                            fontWeight="700"
                            _hover={{ color: NAVY, bg: 'transparent', textDecoration: 'underline' }}
                            h="24px"
                            px="6px"
                        >
                            Clear all
                        </Button>
                    </Flex>
                )}

                {/* ── BULK ACTION BAR ── */}
                {hasSelection && (
                    <BulkActionBar
                        selectedOrders={selectedOrders}
                        onClear={clearSelection}
                    />
                )}

                {/* ── BODY ── */}
                {showSkeleton ? (
                    <>
                        <Box display={{ base: 'none', lg: 'block' }}>
                            <TableSkeleton count={6} />
                        </Box>
                        <Box display={{ base: 'block', lg: 'none' }}>
                            <CardSkeleton count={4} />
                        </Box>
                    </>
                ) : !hasResults ? (
                    <EmptyState
                        hasFilters={hasActiveFilters}
                        onClearAll={() => { setLocalSearch(''); clearAllFilters(); }}
                    />
                ) : (
                    <Box opacity={loading ? 0.6 : 1} transition="opacity 0.2s ease">
                        {/* Desktop table */}
                        <Box display={{ base: 'none', lg: 'block' }}>
                            <OrdersTable
                                orders={orders}
                                selectedIds={selectedIds}
                                isAllSelected={isAllSelected}
                                onToggleSelect={toggleSelection}
                                onToggleSelectAll={toggleSelectAll}
                                onMakePayment={handleMakePayment}
                            />
                        </Box>

                        {/* Mobile cards */}
                        <Flex
                            display={{ base: 'flex', lg: 'none' }}
                            direction="column"
                            gap="10px"
                        >
                            {orders.map((order) => (
                                <OrderCard
                                    key={order.order_id}
                                    order={order}
                                    isSelected={selectedIds.has(order.order_id)}
                                    onToggleSelect={toggleSelection}
                                    onMakePayment={handleMakePayment}
                                />
                            ))}
                        </Flex>

                        {/* Count footer */}
                        <Flex
                            mt="16px"
                            p="12px 16px"
                            justify="center"
                            align="center"
                            color={MUTED}
                            fontSize="11px"
                            fontWeight="600"
                        >
                            <Text>
                                Showing {orders.length.toLocaleString('en-NG')} of {totalCount.toLocaleString('en-NG')} orders
                            </Text>
                        </Flex>
                    </Box>
                )}
            </Box>

            {/* Make payment modal */}
            <MakePaymentModal
                isOpen={!!paymentModalOrder}
                closeModal={closePaymentModal}
                orderDetails={paymentModalOrder}
            />
        </Box>
    );
};

export default Orders;